import { useState, useEffect, useCallback } from "react";
import { cloneDeep, pick, omit, set, mapValues, lowerCase } from "lodash";
import { parsePhoneNumber } from "libphonenumber-js";
import { ORERR_SCHEMA } from "src/_modules/MapitShip/validationSchemas";
import { useSelector } from "react-redux";
import { useGetAllCitiesQuery } from "src/_api/mapitShipApi";

const DEFAULT_BOX_TEMPLATE = {
  dimensions: { length: "", width: "", height: "" },
  weight: 1,
};

/**
 * Normalizes a box template with default values
 */
const normalizeBoxTemplate = (box) => {
  const template = pick(cloneDeep(box ?? DEFAULT_BOX_TEMPLATE), [
    "dimensions",
    "weight",
    "boxName",
  ]);
  template.dimensions = {
    length: template?.dimensions?.length ?? "",
    width: template?.dimensions?.width ?? "",
    height: template?.dimensions?.height ?? "",
  };
  const numericWeight = Number(template?.weight);
  template.weight =
    Number.isFinite(numericWeight) && numericWeight > 0 ? numericWeight : 1;
  return template;
};

/**
 * Applies box totals (weight and package count) to an order
 */
const applyBoxTotals = (order) => {
  console.log("Applying box totals for order:", order);
  const boxes = order?.boxes ?? [];
  const totalWeight = boxes.reduce((acc, box) => {
    const weight = Number(box?.weight);
    return acc + (Number.isFinite(weight) ? weight : 0);
  }, 0);
  const totalBoxes = boxes.length;
  set(order, "weight", totalBoxes > 0 ? totalWeight : 0);
  set(order, "packagesCount", totalBoxes);
};

/**
 * Formats phone number with country code
 */
const formatPhoneWithCode = (phoneCode, phone) => {
  if (!phoneCode || !phone) return phone;
  const trimmedCode = `${phoneCode}`.trim();
  const normalizedCode = trimmedCode.startsWith("+")
    ? trimmedCode
    : `+${trimmedCode}`;
  try {
    const phoneNumber = parsePhoneNumber(`${normalizedCode}${phone}`, {
      extract: true,
    });
    return phoneNumber ? phoneNumber.format("E.164") : phone;
  } catch (error) {
    return phone;
  }
};

/**
 * Validates a specific field path in the data using Joi schema
 */
const validateField = (schema, data, fieldPath) => {
  try {
    const result = schema.validate(data, { abortEarly: false });
    if (!result.error) return false; // No error means valid, so hide card (return false)

    // Debug logging for phone validation
    // if (fieldPath === 'user.phone' && result.error) {
    //     const phoneErrors = result.error.details.filter(d => d.path.join('.').includes('phone'))
    //     if (phoneErrors.length > 0) {
    //         console.log('Phone validation debug:', {
    //             phoneValue: data?.user?.phone,
    //             errors: phoneErrors.map(e => ({ path: e.path.join('.'), message: e.message }))
    //         })
    //     }
    // }

    // Check if any error is related to the specific field path
    const hasError = result.error.details.some((detail) => {
      const errorPath = detail.path.join(".");
      return errorPath.startsWith(fieldPath) || fieldPath.startsWith(errorPath);
    });

    return hasError;
  } catch (error) {
    return true; // On validation error, show the card
  }
};

/**
 * Computes which cards should be hidden based on validation
 */
const computeHideCards = (draftOrder, originalOrder, hasWarehouseIssue) => {

  const hasOrderDetailsError = validateField(
    ORERR_SCHEMA,
    draftOrder,
    "amountPayable",
  );
  const hasWarehouseError =
    validateField(ORERR_SCHEMA, draftOrder, "warehouse") || hasWarehouseIssue;
  const hasContactDetailsError =
    validateField(ORERR_SCHEMA, draftOrder, "user.name") ||
    validateField(ORERR_SCHEMA, draftOrder, "user.phone");
  const hasAddressError =
    validateField(ORERR_SCHEMA, draftOrder, "user.address.street") ||
    validateField(ORERR_SCHEMA, draftOrder, "user.address.area") ||
    validateField(ORERR_SCHEMA, draftOrder, "user.address.city") ||
    validateField(ORERR_SCHEMA, draftOrder, "user.address.landmark") ||
    validateField(ORERR_SCHEMA, draftOrder, "user.address.houseNumber") ||
    validateField(ORERR_SCHEMA, draftOrder, "user.address.postcode");
  const hasMapLocationError = validateField(
    ORERR_SCHEMA,
    draftOrder,
    "user.location",
  );
  const hasLandmarkError = validateField(
    ORERR_SCHEMA,
    draftOrder,
    "user.address.landmark",
  );
  const hasBoxesError = validateField(ORERR_SCHEMA, draftOrder, "boxes");
  const hasOrderItemsError = validateField(
    ORERR_SCHEMA,
    draftOrder,
    "orderItems",
  );

  console.log("Validation Errors:", {
    hasOrderDetailsError,
    hasWarehouseError,
    hasContactDetailsError,
    hasAddressError,
    hasMapLocationError,
    hasLandmarkError,
    hasBoxesError,
    hasOrderItemsError,
  });

  return {
    orderDetailsCard: !hasOrderDetailsError,
    orderBoxesCard:
      Boolean(originalOrder?.otoIntegration?.boxes?.length) && !hasBoxesError,
    orderItemsCard:
      Boolean(originalOrder?.orderItems?.length) && !hasOrderItemsError,
    warehouseCard: !hasWarehouseError,
    contactDetails: !hasContactDetailsError,
    addressCard: !hasAddressError,
    mapLocationCard: !hasMapLocationError,
    landmark: !hasLandmarkError,
  };
};

/**
 * Custom hook to format MapitShip orders asynchronously
 *
 * @param {Array} orders - Array of orders to format
 * @param {Object} defaultBox - Default box template to use
 * @returns {Object} { formattedOrders, isLoading, hideCardsMap, ordersWithHiddenCards }
 */
export const useFormatMapitShipOrders = (
  orders,
  defaultBox,
  skip,
  warehouseIssues,
) => {
  console.log(warehouseIssues);

  const [formattedOrders, setFormattedOrders] = useState([]);
  const [hideCardsMap, setHideCardsMap] = useState({});
  const [ordersWithHiddenCards, setOrdersWithHiddenCards] = useState(undefined);
  const [isLoading, setIsLoading] = useState(false);

  // const [getCities] = useLazyGetCitiesQuery()
  const { data: cities } = useGetAllCitiesQuery();

  /**
   * Formats a single order payload
   */
  const formatSingleOrder = useCallback(
    async (order, boxTemplate) => {
      if (!order) return null;

      const payload = {
        boxes: !!order?.otoIntegration?.boxes?.length
          ? cloneDeep(order.otoIntegration.boxes)
          : !!boxTemplate
            ? [boxTemplate]
            : [DEFAULT_BOX_TEMPLATE],
        ...pick(cloneDeep(order), [
          "id",
          "packagesCount",
          "weight",
          "amountPayable",
          "warehouse",
          "orderItems",
          "user",
        ]),
      };

      // Format order items
      if (payload.orderItems?.length) {
        payload.orderItems = payload.orderItems.map((item) =>
          omit(item, ["_id", "id"]),
        );
      }

      // Format warehouse
      if (payload.warehouse) {
        payload.warehouse = payload.warehouse?.id ?? payload.warehouse;
      }

      // Format phone number
      if (payload.user?.phone && payload.user?.phoneCode) {
        payload.user.phone = formatPhoneWithCode(
          payload.user.phoneCode,
          payload.user.phone,
        );
        delete payload.user.phoneCode;
      }

      // Apply box template
      payload.boxes = payload.boxes.map((box) => normalizeBoxTemplate(box));

      // Format order items or create default
      if (payload.orderItems?.length) {
        payload.orderItems = payload.orderItems.map((item) => ({
          item: item.item,
          sku: item?.sku ?? "",
          amount: item?.amount ?? "",
          price: item?.price ?? "",
        }));
      } else {
        payload.orderItems = [{ item: "", sku: "", amount: "", price: "" }];
      }

      try {
        // Validate and normalize city name
        const inputCity = `${payload?.user?.address?.city ?? ""}`.trim();
        // const isEnglish = /^[A-Za-z\s-]+$/.test(inputCity)
        let city = undefined;

        if (!!inputCity) {
          city = cities?.find(
            (city) => lowerCase(city.name_en) === lowerCase(inputCity),
          );
        }

        if (!!city) {
          set(payload, "user.address.city", city.name_en);
        } else {
          set(payload, "user.address.city", "");
        }
      } catch (error) {
        console.error("Failed to validate city:", error);
        set(payload, "user.address.city", "");
      }

      return omit(payload, ["user.address.floor", "user.address.flat"]);
    },
    [cities],
  );

  /**
   * Process orders sequentially with async operations
   */

  useEffect(() => {
    const processOrders = async () => {
      // Skip processing if skip is true for better performance
      if (skip) {
        setFormattedOrders([]);
        setHideCardsMap({});
        setOrdersWithHiddenCards(undefined);
        setIsLoading(false);
        return;
      }

      if (!orders?.length) {
        setFormattedOrders([]);
        setHideCardsMap({});
        setOrdersWithHiddenCards(undefined);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        const formatted = [];
        const hideCards = {};
        const ordersWithHidden = [];

        // Process each order sequentially
        for (let i = 0; i < orders.length; i++) {
          const order = orders[i];

          // Format the order
          const formattedOrder = await formatSingleOrder(order, defaultBox);

          if (formattedOrder) {
            // Apply box totals
            applyBoxTotals(formattedOrder);

            // Compute hide cards for this order
            //const orderDraft = omit(cloneDeep(formattedOrder), ['id'])
            const hasWarehouseIssue = warehouseIssues?.find(
              (issue) =>
                issue?.id === order?.id ,
            );

            const hideCardsForOrder = computeHideCards(
              formattedOrder,
              order,
              hasWarehouseIssue,
            );

            // Store results
            formatted.push(formattedOrder);
            hideCards[i] = hideCardsForOrder;

            // Check if this order has at least one card that is NOT hidden (shown)
            const hasShownCard = Object.values(hideCardsForOrder).some(
              (isHidden) => isHidden === false,
            );
            if (hasShownCard) {
              ordersWithHidden.push({
                index: i,
                order: formattedOrder,
                hideCards: hideCardsForOrder,
              });
            }
          }
        }

        setFormattedOrders(formatted);
        setHideCardsMap(hideCards);
        setOrdersWithHiddenCards(ordersWithHidden);
        setIsLoading(false);
      } catch (error) {
        console.error("Failed to format MapitShip orders:", error);
        setFormattedOrders([]);
        setHideCardsMap({});
        setOrdersWithHiddenCards(undefined);
        setIsLoading(false);
      }
    };

    processOrders();
  }, [orders, defaultBox, formatSingleOrder, skip, warehouseIssues]);

  return {
    formattedOrders,
    hideCardsMap,
    ordersWithHiddenCards,
    isLoading,
  };
};
